import { useEffect, useRef, useState } from "react"
import { deserialize } from "jsonapi-deserializer"
import { useDispatch, useSelector } from "react-redux"

import consumer from "~/channels/consumer"
import { useActiveSpacePage } from "~/providers/ActiveSpacePageProvider"
import { postEvent } from "~/utils/EventManager"
import { getUser, setCycle, setUser, updateSpace } from "~/store"
import { Subscription } from "@rails/actioncable"

export default function SpaceChannel() {
  const dispatch = useDispatch()
  const subscription = useRef<Subscription | null>(null)
  const user = useSelector(getUser)
  const channelSpaceName = useActiveSpacePage()?.channelSpaceName ?? null
  const [joinedSpaceName, setJoinedSpaceName] = useState<string | null>(null)

  const spaceName = channelSpaceName ?? user?.space_name ?? null
  // A space page always joins, since user.space_name can be set before the
  // server has made the user a member (e.g. right after creating a space).
  // Once joined, moving to other pages keeps that subscription instead of
  // resubscribing (and broadcasting) on every click.
  const join =
    channelSpaceName !== null ||
    (spaceName !== null && spaceName === joinedSpaceName)

  useEffect(() => {
    if (!spaceName || !user) return

    if (join) setJoinedSpaceName(spaceName)

    subscription.current = consumer.subscriptions.create(
      { channel: "SpaceChannel", spaceName, join },
      {
        connected() {
          if (join) {
            dispatch(setUser({ ...user, space_name: spaceName }))
            postEvent({ spaceName }, "userConnected")
          }
        },

        received(data) {
          const spaceData = data.value ? deserialize(data.value) : {}
          if (spaceData.current_timer) {
            if (spaceData.current_timer.break) dispatch(setCycle("break"))
            else dispatch(setCycle("work"))
          }

          if (data.type === "timerEnded") {
            dispatch(
              setCycle(data.timer.data.attributes.break ? "work" : "break"),
            )
            spaceData.current_timer = null
          }

          dispatch(
            updateSpace({
              name: spaceName,
              changes: spaceData,
            }),
          )
        },
      },
    )
    return () => {
      subscription.current?.unsubscribe()
    }
  }, [spaceName, join])

  return null
}
